import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../button/Button';
import {
  isCurrentTimeAfterEvent,
  getDateLabel,
  getSlotColor,
  redirectUserToSchedulePageWithReload,
  checkIsConfigNeedsToBeRefreshed,
} from '../../utils';
import Dialog from '../dialog/Dialog';
import '../entertainmentOptions/EntertainmentOptions.scss';
import ArrivalTimeOption from '../arrivalTimeOption/ArrivalTimeOption';

const getOptionDisabled = (
  checked,
  areSpacesAvailable,
  singleDate = '',
  duration = 0,
  isAlreadyBooked,
) => !checked && !isAlreadyBooked && (!areSpacesAvailable || isCurrentTimeAfterEvent(singleDate, duration));

const onOptionClick = (
  disabled,
  code,
  setSelectedArrivalTime,
) => {
  if (disabled) return;
  setSelectedArrivalTime(code);
};

const onCancelButton = async (
  getAvailableArrivalTimeSlots,
  removeArrivalTimeSlotFromBooking,
  isShorexEnhancementEnabled,
  bookedArrivalSlotId,
  selectedArrivalSlotId,
  setSelectedArrivalTime,
) => {
  if (bookedArrivalSlotId === 0 && selectedArrivalSlotId !== 0) {
    setSelectedArrivalTime(0);
    return;
  }
  if (!isShorexEnhancementEnabled) await getAvailableArrivalTimeSlots(true);
  removeArrivalTimeSlotFromBooking();
};

const onBookButton = async (
  addArrivalTimeSlotToBooking,
  refreshTimeInterval,
  lastConfigCheckDateTime,
  setShowReloadIsNecessaryDialog,
  getAvailableArrivalTimeSlots,
  isShorexEnhancementEnabled = false,
) => {
  const isReloadNecessary = checkIsConfigNeedsToBeRefreshed(lastConfigCheckDateTime, refreshTimeInterval);
  if (isReloadNecessary) {
    setShowReloadIsNecessaryDialog(true);
    return;
  }
  if (!isShorexEnhancementEnabled) await getAvailableArrivalTimeSlots(true);
  addArrivalTimeSlotToBooking();
};

const ArrivalTimeOptions = ({
  title = 'Setup your experience',
  options = [],
  quantityToBook = 1,
  setSelectedArrivalTime,
  selectedArrivalSlotId,
  bookedArrivalSlotId,
  addArrivalTimeSlotToBooking,
  isArrivalTimeEditable = false,
  removeArrivalTimeSlotFromBooking,
  refreshTimeInterval,
  lastConfigCheckDateTime,
  getAvailableArrivalTimeSlots,
  isShorexEnhancementEnabled,
}) => {
  const [showReloadIsNecessaryDialog, setShowReloadIsNecessaryDialog] = useState(false);
  const showCancelButton = !!selectedArrivalSlotId && isArrivalTimeEditable;

  return (
    <>
      {showReloadIsNecessaryDialog && (
        <Dialog
          messages={[`You've been inactive on the page for more than ${refreshTimeInterval} mins, your page will refreshed`]}
          positiveButtonText="OK"
          onPositiveButtonClick={redirectUserToSchedulePageWithReload}
          hideNegativeButton
        />
      )}
      <div className="title-container">
        <div className="options-title">{title}</div>
      </div>
      <div className="options-container">
        {!!options && options.map((option, index) => {
          const { code, remainingCapacity = 1, singleDate, duration = 0 } = option;
          const isChecked = code === selectedArrivalSlotId;
          const areSpacesAvailable = remainingCapacity !== 0 && quantityToBook <= remainingCapacity;
          const isAlreadyBooked = code === bookedArrivalSlotId;
          const disabled = getOptionDisabled(
            isChecked,
            areSpacesAvailable,
            singleDate,
            duration,
            isAlreadyBooked,
          );
          return (
            <div key={index}>
              <ArrivalTimeOption
                key={index}
                label={getDateLabel(option)}
                checked={isChecked}
                color={getSlotColor(index)}
                disabled={disabled}
                onOptionClick={() => onOptionClick(
                  disabled,
                  code,
                  setSelectedArrivalTime,
                )}
                areSpacesAvailable={areSpacesAvailable}
                isAlreadyBooked={isAlreadyBooked}
              />
            </div>
          );
        })}
      </div>
      <div className="book-time-btn">
        <Button
          text="Book time"
          disabled={!selectedArrivalSlotId || !isArrivalTimeEditable}
          fontSize={14}
          onClick={() => onBookButton(
            addArrivalTimeSlotToBooking,
            refreshTimeInterval,
            lastConfigCheckDateTime,
            setShowReloadIsNecessaryDialog,
            getAvailableArrivalTimeSlots,
            isShorexEnhancementEnabled,
          )}
        />
      </div>
      {showCancelButton && (
        <div
          className="cancel-booking-container"
          onClick={() => onCancelButton(
            getAvailableArrivalTimeSlots,
            removeArrivalTimeSlotFromBooking,
            isShorexEnhancementEnabled,
            bookedArrivalSlotId,
            selectedArrivalSlotId,
            setSelectedArrivalTime,
          )}
        >
          {!!selectedArrivalSlotId && (
            <div className="cancel-booking">Cancel booking</div>
          )}
        </div>
      )}
    </>
  );
};

ArrivalTimeOptions.propTypes = {
  title: PropTypes.string,
  options: PropTypes.array,
  quantityToBook: PropTypes.number,
  setSelectedArrivalTime: PropTypes.func,
  selectedArrivalSlotId: PropTypes.number,
  bookedArrivalSlotId: PropTypes.number,
  addArrivalTimeSlotToBooking: PropTypes.func,
  isArrivalTimeEditable: PropTypes.bool,
  removeArrivalTimeSlotFromBooking: PropTypes.func,
  refreshTimeInterval: PropTypes.number,
  lastConfigCheckDateTime: PropTypes.string,
  getAvailableArrivalTimeSlots: PropTypes.func,
  isShorexEnhancementEnabled: PropTypes.bool,
};

export default ArrivalTimeOptions;
